import initialState from './initial.state';
import * as types from '../actions/types.action';
import {
  ANALYTICS_EVENTS,
  BASKET_ERRORS_TYPE,
  ROUTES,
} from '../constants/constants';
import {
  GoogleAnalyticsHelper,
  summarizeAction,
} from '../helpers/googleAnalytics.helper';
import {
  isOrderAwaitingPayment,
  isOrderConfirmed,
} from '../helpers/order.helper';

export default function analyticsReducer(
  state = initialState.analytics,
  action
) {
  switch (action.type) {
    case types.UPDATE_SETTING_REQUESTED:
      if (action.setting && action.value) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SET_SETTINGS, {
          setting: action.setting,
          value: action.value,
        });
      }
      return state;

    case types.SET_FULFILL_SLOT_REQUESTED:
      if (action.uuid && action.fulfillmentType) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SET_FULFILLMENT, {
          basketUuid: action.uuid,
          fulfillmentType: action.fulfillmentType,
        });
      }
      return state;

    // Catch weight box picker. Never send box numbers, only counts.
    case types.OPEN_BOX_PICKER_SUCCESS:
      (action.pickers || []).forEach(picker => {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_PICKER_OPEN, {
          item_id: picker.itemId,
          source: picker.source,
        });
        if (picker.boxes && picker.boxes.promiseOnOrder) {
          GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_PROMISE_SHOWN, {
            item_id: picker.itemId,
          });
        }
      });
      return state;
    case types.PICK_BOXES_REQUESTED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_PICK_ATTEMPT, {
        item_id: action.itemId,
        boxes: (action.boxNumbers || []).length,
        quantity: action.analytics && action.analytics.quantity,
        value: action.analytics && action.analytics.value,
      });
      return state;
    case types.PICK_BOXES_SUCCEEDED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_PICK_SUCCESS, {
        item_id: action.itemId,
        boxes: ((action.line && action.line.boxNumbers) || []).length,
        quantity: action.line && action.line.quantity,
        value: action.line && action.line.price,
      });
      return state;
    case types.PICK_BOXES_FAILED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_PICK_FAILURE, {
        item_id: action.itemId,
        reason: action.reason,
      });
      return state;
    case types.UPDATE_LINE_BOXES_REQUESTED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.BOX_REMOVE, {
        item_id: action.itemId,
        boxes: (action.boxNumbers || []).length,
      });
      return state;
    case types.BOX_CHECKOUT_UNAVAILABLE:
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.BOX_CHECKOUT_UNAVAILABLE,
        {}
      );
      return state;

    case types.UPDATE_BASKET_ITEM_REQUESTED:
      GoogleAnalyticsHelper.trackEvent(
        action.type.toLowerCase().replaceAll('.', '_'),
        {
          itemUuid: action.uuid,
          quantity: action.params && action.params.quantity,
        }
      );
      return state;

    case types.REMOVE_BASKET_SUCCEEDED: {
      const basket = GoogleAnalyticsHelper.STORE.basket || {};
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.REMOVE_BASKET, {
        axBasketId: basket.axBasketId,
        basketUuid: basket.uuid,
        branchId: basket.branchId,
        fulfillmentType: basket.fulfillmentType,
        itemCount: basket.items ? basket.items.length : 0,
        // basket.subTotal never existed on the real basket shape (confirmed
        // via live cross-platform testing on 2026-09-02 — totalValue was
        // always undefined/dropped from every REMOVE_BASKET event on every
        // platform); the real field, used everywhere else in this codebase
        // (PriceSummary, CheckoutPage), is basket.price.total.
        totalValue: basket.price?.total,
      });
      return state;
    }

    case types.ADD_TO_FAVOURITES_REQUESTED:
      if (action.product) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.ADD_FAVOURITE, {
          product: action.product,
        });
      }
      return state;

    case types.REMOVE_FROM_FAVOURITES_REQUESTED:
      if (action.product) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.REMOVE_FAVOURITE, {
          product: action.product,
        });
      }
      return state;

    case types.CHECKOUT_SLOT_EXPIRED:
      const issue =
        action.page === ROUTES.CHECKOUT
          ? BASKET_ERRORS_TYPE.SLOT_EXPIRED_DURING_CHECKOUT
          : BASKET_ERRORS_TYPE.SLOT_EXPIRED;
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.CHECKOUT_SLOT_EXPIRED, {
        issue,
      });
      return state;

    case types.PROCESS_CHECKOUT_REQUESTED:
      if (action.basketUuid) {
        const step = state && state.step;
        if (!step) {
          return {
            ...state,
            step: 1,
          };
        }
        return state;
      }
      return state;

    case types.PROCESS_CHECKOUT_SUCCESS:
      if (action.checkout) {
        const step = state && state.step;
        if (step === 1) {
          return {
            ...state,
            step: 2,
          };
        }
        return state;
      }
      return state;

    case types.CHECKOUT_PAYMENT_IN_PROGRESS:
      // No order was created: checkout is sending the customer to an earlier,
      // still pending Pay By Bank order. That is not a checkout success, so the
      // step is left where the request put it; CONFIRM_ORDER is fired below
      // only if a checkout from this session is still waiting for that order.
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.PBB_PAYMENT_IN_PROGRESS,
        {
          orderId: action.checkout && action.checkout.orderId,
          basketUuid: action.basketUuid,
        }
      );
      return state;

    case types.GET_ORDER_AFTER_CHECKOUT_REQUESTED:
      if (action.orderId) {
        const step = state && state.step;
        if (step === 2) {
          return {
            ...state,
            step: 3,
          };
        }
        return state;
      }
      return state;

    case types.GET_ORDER_SUCCEEDED:
      const step = state && state.step;
      if (step === 3) {
        if (isOrderAwaitingPayment(action.order)) {
          // Still a draft, or held while the bank sends the money: nothing has
          // been placed, so this is not the purchase. Keep waiting for it.
          return state;
        }
        if (isOrderConfirmed(action.order)) {
          GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.CONFIRM_ORDER, {
            orderId: action.order.orderId,
            items: action.order.items,
            fulfillmentType: action.order.fulfillmentType,
            currency: 'GBP',
            value: action.order.price && action.order.price.total,
          });
          GoogleAnalyticsHelper.clearSession();
        }
        // Expired, voided, cancelled or payment required: the checkout ended
        // without a confirmed order. The confirmation page reports those.
        return {
          ...state,
          step: null,
        };
      }
      // Attribution consumed by the checkout — clear so the next session
      // starts fresh.
      GoogleAnalyticsHelper.setSearchContext(null);
      return state;

    case types.GET_BASKET_SUCCEEDED:
      GoogleAnalyticsHelper.setFulfillment(action.basket.fulfillmentType);
      GoogleAnalyticsHelper.updateStore({basket: action.basket});
      return state;

    case types.GET_SEARCH_RESULTS_REQUESTED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SEARCH, action.params);
      return state;

    case types.GET_SEARCH_RESULTS_SUCCESS:
      // graph-product-search mints a searchId for every product query, not just keyword search —
      // favourites, previously-purchased, offers and the basket carousels all carry their own now.
      // This case is still specifically the keyword-search one, so treat it as the canonical "new
      // attribution entry" and overwrite any persisted context. Category browses / empty queries
      // return searchId=null and leave the previous context untouched.
      //
      // Downstream: a searchId on an event no longer implies the customer typed something. Join to
      // the impression's `operation` to tell the surfaces apart — see
      // graph-product-search/docs/search-impression-log.md.
      if (action.searchId) {
        GoogleAnalyticsHelper.setSearchContext({
          searchId: action.searchId,
          searchPosition: null,
        });
      }
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SEARCH_RESULTS, {
        results: action.searchResults.length,
        searchId: action.searchId,
      });
      // Peer-gap trial exposure. The matching conversions come through the ordinary
      // ANALYTICS_EVENTS.ADD_PRODUCT event, which already carries source/searchId/searchPosition —
      // the two join on searchId.
      if (action.featuredResults && action.featuredResults.length > 0) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.FEATURED_IMPRESSION, {
          searchId: action.searchId,
          campaignId: action.featuredResults[0].campaignId,
          itemIds: action.featuredResults.map(
            featured => featured.product && featured.product.itemId
          ),
          positions: action.featuredResults.map(featured => featured.position),
        });
      }
      // "Showing results for milk": the search found nothing and was answered with a correction.
      // Only for a client-side search - the server render carries no searchId and sends nothing.
      if (action.correctedQuery && action.searchId) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SEARCH_CORRECTED, {
          searchId: action.searchId,
          query: action.query,
          correctedQuery: action.correctedQuery,
        });
      }
      return state;

    case types.SEARCH_INSTEAD_CLICKED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SEARCH_INSTEAD_CLICK, {
        searchId: action.searchId,
        query: action.query,
        correctedQuery: action.correctedQuery,
      });
      return state;

    case types.RELATED_PRODUCT_CLICKED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.RELATED_PRODUCT_CLICK, {
        searchId: action.searchId,
        parentSearchId: action.parentSearchId,
        strategy: action.strategy,
        itemId: action.itemId,
        searchPosition: action.searchPosition,
      });
      return state;

    case types.GET_RELATED_PRODUCTS_SUCCESS:
      // Exposure for the related strip. Its adds come through ADD_PRODUCT with source
      // RELATED_SEARCH and this searchId; parentSearchId joins it to the search it sat under.
      if (action.results && action.results.length > 0) {
        GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.RELATED_IMPRESSION, {
          searchId: action.searchId,
          parentSearchId: action.parentSearchId,
          strategy: action.strategy,
          itemIds: action.results.map(product => product.itemId),
        });
      }
      return state;

    case types.REGISTER_USER_FAILED:
    case types.REGISTER_USER_SUCCESS:
      let messageData = {
        message: action.message,
        messages: action.messages && action.messages.data,
      };
      const actionType =
        action.messages &&
        action.messages.data &&
        action.messages.data.Data &&
        action.messages.data.Data.Created
          ? types.REGISTER_USER_SUCCESS
          : types.REGISTER_USER_FAILED;
      GoogleAnalyticsHelper.trackEvent(actionType, {
        payload: Object.assign({}, action.payload),
        ...(actionType === types.REGISTER_USER_FAILED && messageData),
      });
      return state;

    case types.SET_PAYMENT_TYPE_SUCCESS:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.SET_PAYMENT_TYPE, {
        paymentMethod: action.basket.paymentMethod,
      });
      return state;

    case types.GET_PRODUCT_DETAIL_SUCCESS:
      GoogleAnalyticsHelper.updateStore({productDetail: action.productDetail});
      return state;

    case types.SEND_REFUND_EMAIL_REQUESTED:
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.REQUEST_REFUND, {
        items: action.items,
        accountDetails: action.accountDetails,
        axBasketId: action.axBasketId,
      });
      return state;

    case types.SET_BRANCH_SUCCESS:
      if (state?.branch !== action.branch) {
        // action.branch is a plain branchId string at most call sites, but an
        // object at a few — normalize so summarizeAction (which drops object
        // values) never silently empties this event, and use `branchId` to
        // match the naming REMOVE_BASKET already uses for the same concept.
        const branchId =
          action.branch && typeof action.branch === 'object'
            ? action.branch.branchId || action.branch.id
            : action.branch;
        GoogleAnalyticsHelper.trackEvent(
          action.type.toLowerCase().replaceAll('.', '_'),
          {branchId}
        );
        return {...state, branch: action.branch};
      }
      return state;
    case types.EDIT_ORDER_REQUESTED:
    case types.ADD_VOUCHER_REQUESTED:
    case types.ADD_VOUCHER_FAILED:
    case types.REMOVE_VOUCHER_REQUESTED:
    case types.REMOVE_VOUCHER_SUCCEEDED:
    case types.REMOVE_VOUCHER_FAILED:
    case types.SET_SUBSTITUTE_ITEM_SUCCEEDED:
    case types.SET_SUBSTITUTE_ITEM_IN_BASKET_SUCCEEDED:
    case types.CANCEL_ORDER_REQUESTED:
    case types.CANCEL_ORDER_SUCCEEDED:
    case types.CANCEL_ORDER_FAILED:
    case types.GET_NEAREST_BRANCH_SUCCESS:
    case types.GET_RECOMMENDED_ITEMS_SUCCESS:
    case types.GET_UPSELLING_ITEMS_SUCCESS:
    case types.GET_FULFILL_SLOT_SUCCESS:
    case types.SET_FULFILL_SLOT_SUCCESS:
    case types.DELETE_FULFILL_SLOT_SUCCESS:
    case types.TOGGLE_SWITCH_ACCOUNT_MODAL_SUCCESS:
    case types.TOGGLE_UPSELLING_MODAL_SUCCESS:
    case types.TOGGLE_SUBSTITUTES_MODAL_SUCCESS:
    case types.TOGGLE_STOP_EDIT_MODAL_SUCCESS:
    case types.TOGGLE_AVAILABLE_ONLY_MODAL_SUCCESS:
    case types.TOGGLE_CHANGE_BRANCH_MODAL_SUCCESS:
    case types.TOGGLE_OPEN_IN_JJS_MODAL_SUCCESS:
    case types.TOGGLE_MAINTENANCE_MODAL_SUCCESS:
    case types.TOGGLE_RELOGIN_MODAL_SUCCESS:
    case types.TOGGLE_PAYMENT_DETAILS_MODAL_SUCCESS:
    case types.TOGGLE_NEW_OFFERS_MODAL_SUCCESS:
    case types.TOGGLE_CATCH_WEIGHT_MODAL_SUCCESS:
    case types.TOGGLE_BASKET_SYNC_FAILED_MODAL_SUCCESS:
    case types.TOGGLE_CUSTOMER_SUPPORT_MODAL_SUCCESS:
    case types.TOGGLE_RATING_MODAL_SUCCESS:
    case types.ADD_TO_FAVOURITES_SUCCESS:
    case types.REMOVE_FROM_FAVOURITES_SUCCESS:
    case types.GET_PREVIOUSLY_PURCHASED_REQUESTED:
    case types.GET_MARKETING_OPTIONS_SUCCESS:
    case types.UPDATE_MARKETING_OPTIONS_SUCCESS:
    case types.GET_SUBSTITUTES_SUCCEED:
    case types.REGISTER_USER_REQUESTED:
    case types.PASSWORD_RESET_REQUESTED:
    case types.SET_NEW_PASSWORD_REQUESTED:
    case types.CHANGE_PASSWORD_REQUESTED:
    case types.ACTIVATE_ACCOUNT_REQUESTED:
    case types.GET_PAYMENT_DETAILS_REQUESTED:
    case types.SET_PAYMENT_DETAILS_SUCCESS:
    case types.CONTACT_CUSTOMER_SERVICE_REQUESTED:
    case types.TRACK_ORDER_REQUESTED:
    case types.REAUTH_ORDER_REQUESTED:
    case types.ADD_TO_SHOPPING_LIST_REQUESTED:
    case types.REMOVE_FROM_SHOPPING_LIST_REQUESTED:
    case types.RENAME_SHOPPING_LIST_REQUESTED:
    case types.DELETE_SHOPPING_LIST_REQUESTED:
    case types.RESET_SHOPPING_LIST_SUCCESS:
    case types.SET_USE_DEFAULT_PBB_BANK_SUCCESS:
    case types.SET_AUTH_SUCCESS:
      GoogleAnalyticsHelper.trackEvent(
        action.type.toLowerCase().replaceAll('.', '_'),
        summarizeAction(action)
      );
      return state;
    default:
      return state;
  }
}
